import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Phone, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

export default function Book() {
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setCalendlyUrl(r.data.calendly_url || "")).finally(() => setChecked(true));
  }, []);

  // Inject Calendly widget script when we have a URL
  useEffect(() => {
    if (!calendlyUrl) return;
    const id = "calendly-widget-script";
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id;
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
    const linkId = "calendly-widget-css";
    if (!document.getElementById(linkId)) {
      const l = document.createElement("link");
      l.id = linkId;
      l.rel = "stylesheet";
      l.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(l);
    }
  }, [calendlyUrl]);

  return (
    <div data-testid="book-page">
      <Seo
        title="Book a Consultation | Mike Sidhu, Personal Real Estate Corporation · RE/MAX Performance Realty"
        description="Book a 30-minute consultation with Mike Sidhu, Personal Real Estate Corporation — foreclosure and court-ordered real estate in British Columbia. Real availability, no double bookings."
        path="/book"
      />

      <section className="bg-navy pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal><p className="overline">Consultation</p></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white mt-4 leading-[1.05]">
                Book a private call with <span className="italic text-brass">Mike Sidhu, Personal Real Estate Corporation</span>.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body text-white/70 text-lg mt-6 max-w-2xl leading-relaxed">
                Select a time that suits you. Slots reflect Mike's real calendar, so double-bookings are impossible.
                You'll receive a confirmation email immediately, and Mike will be notified for every booking.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div className="border-l-2 border-brass pl-5 py-2">
              <p className="overline mb-1">Prefer a call?</p>
              <a href="tel:+16048312500" className="font-heading text-2xl text-white hover:text-brass transition-colors flex items-center gap-2">
                <Phone size={18} className="text-brass" /> 604-831-2500
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {calendlyUrl ? (
            <div
              data-testid="calendly-embed"
              className="calendly-inline-widget bg-white border border-navy/10"
              data-url={calendlyUrl}
              style={{ minWidth: "320px", height: "780px" }}
            />
          ) : checked ? (
            <div data-testid="calendly-placeholder" className="bg-white border border-navy/10 p-10 md:p-14">
              <div className="w-14 h-14 bg-brass text-white flex items-center justify-center">
                <Calendar size={26} />
              </div>
              <p className="overline mt-6">Booking Setup Pending</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-navy mt-3 leading-tight">
                The calendar link is being connected.
              </h2>
              <p className="font-body text-ink-soft text-lg mt-5 max-w-2xl leading-relaxed">
                Live scheduling opens once Mike's Calendly account is linked. In the meantime, call
                <a href="tel:+16048312500" className="text-navy underline decoration-brass underline-offset-4 mx-1">604-831-2500</a>
                or send a request and Mike will confirm a time by return.
              </p>
              <Link
                to="/contact"
                data-testid="book-fallback-cta"
                className="inline-flex items-center gap-2 mt-8 bg-navy text-white px-8 py-4 hover:bg-brass transition-colors duration-300 font-body text-sm tracking-wide"
              >
                Request a Time <ArrowRight size={17} />
              </Link>
            </div>
          ) : (
            <p className="font-mono text-ink-soft text-sm">Loading calendar…</p>
          )}
        </div>
      </section>
    </div>
  );
}
